'use client';

import MenuBookIcon from '@mui/icons-material/MenuBook';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography,
  useTheme,
} from '@mui/material';
import { useState, type MouseEvent } from 'react';

const POPOVER_ID = 'help-resources-popover';

/**
 * The panel width used by the risk assessment app's Help & Resources panel. No size token has
 * this value: the nearest, size 2xs and size xs, are 15.625rem and 21.875rem.
 */
const PANEL_WIDTH = '17.5rem';

export type THelpResource = {
  /** Stable list key. Not shown to the user. */
  key: string;
  label: string;
  /** Absolute external URL. Opens in a new tab. */
  href: string;
};

export type THelpResourcesProps = {
  /** Display order is list order. */
  resources: THelpResource[];
  /** The "Browse full Help Centre" target. */
  helpCentreUrl: string;
};

const externalLinkProps = {
  component: 'a',
  target: '_blank',
  rel: 'noopener noreferrer',
} as const;

// The panel's width and its inner spacing match the risk assessment app's Help & Resources panel
// (src/components/nav/SideNav/HelpResources.tsx in the
// Greengate-Fintech/verifime-client-portal-risk-assessment repository). The trigger's margins and
// radius follow the nav rows in SideNav.tsx.
// Except for the panel width (the PANEL_WIDTH literal above), the values are read from
// theme.semantic rather than a contextual theme token: no Help & Resources token exists, and the
// panel's own spacing (not the trigger's margins and radius, which follow the nav rows) is the
// one-off case in "The Token Selection Decision Tree" of doc/guidelines/token-usage-guidelines.md
// in the Greengate-Fintech/verifime-adr repository.
export default function HelpResources({ resources, helpCentreUrl }: THelpResourcesProps) {
  const { semantic } = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      {/* Pinned to the rail's 250px, which includes the 1px border on the Drawer paper in
          SideNav.tsx, so the trigger button, which the panel anchors to, ends spacing.xs short of
          the rail's outer (right) edge. */}
      <List sx={{ width: semantic.size['2xs'], boxSizing: 'border-box' }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleOpen}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-controls={open ? POPOVER_ID : undefined}
            sx={{
              marginX: semantic.spacing.xs,
              marginY: semantic.spacing.xs,
              borderRadius: semantic.borderRadius.sm,
            }}
          >
            <ListItemIcon>
              <MenuBookIcon />
            </ListItemIcon>
            <ListItemText primary="Help & Resources" />
          </ListItemButton>
        </ListItem>
      </List>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          id: POPOVER_ID,
          role: 'dialog',
          'aria-label': 'Help and Resources',
          sx: {
            width: PANEL_WIDTH,
            padding: semantic.spacing.xs,
            // The trigger button ends spacing.xs short of the rail's outer edge. spacing.lg here
            // equals spacing.xs + spacing.sm, which leaves a spacing.sm gap from that edge.
            marginLeft: semantic.spacing.lg,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: semantic.spacing.xs,
            paddingLeft: semantic.spacing.xs,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontSize: semantic.fontSize.xs, fontWeight: semantic.fontWeight.semibold }}
          >
            Help &amp; Resources
          </Typography>
          <IconButton onClick={handleClose} size="small" aria-label="Close help and resources">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <List dense sx={{ marginTop: semantic.spacing.xs }}>
          {resources.map((resource) => (
            <ListItem key={resource.key} disablePadding>
              <ListItemButton
                {...externalLinkProps}
                href={resource.href}
                sx={{ borderRadius: semantic.borderRadius.sm, gap: semantic.spacing.xs }}
              >
                <ListItemText primary={resource.label} />
                <OpenInNewIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Divider />

        <ListItemButton
          {...externalLinkProps}
          href={helpCentreUrl}
          sx={{
            borderRadius: semantic.borderRadius.sm,
            marginTop: semantic.spacing['2xs'],
            gap: semantic.spacing.xs,
          }}
        >
          <ListItemText
            primary="Browse full Help Centre"
            primaryTypographyProps={{ color: 'primary', fontWeight: 'medium' }}
          />
          <OpenInNewIcon fontSize="small" color="primary" />
        </ListItemButton>
      </Popover>
    </>
  );
}
